import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import PSHomeSectionEmpty from '@/components/PSHomeSectionEmpty';

/**
 * ── /components/ps-home-section-empty ────────────────────────────────────
 *
 * PSHomeSectionEmpty — PSHomeSectionError's sibling for the OTHER outcome of
 * a section fetch: the request succeeded but came back with zero rows.
 * Nothing failed, so the affordance is "Refresh" (inline PSLinkButton, not
 * a standalone button) rather than a retry. Real call sites, all in
 * `data && list.length === 0 ? <PSHomeSectionEmpty ... /> : <Carousel>...`
 * branches next to a `PSHomeSectionError` sibling for the error branch:
 * home's (`app/index.tsx`) Editorial Picks carousel (light surface — its
 * Access Pass block uses APEmptySection instead); memberships'
 * (`app/memberships/index.tsx`) "What you've missed" showcase and "All
 * plans" list (both `isBackgroundDark` too — memberships never draws this
 * on a light surface).
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function PSHomeSectionEmptyPreview() {
  // Shared by both blocks below so either "Refresh" tap moves the same
  // counter — proves onRefresh actually fires regardless of which surface
  // (light/dark) it was tapped from.
  const [refreshCount, setRefreshCount] = useState(0);

  return (
    <>
      <Head>
        <title>PSHomeSectionEmpty · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Light — home's Editorial Picks, the only light-surface call site.
            Live: tap &quot;Refresh&quot; inline in the sentence and watch the
            counter in the caption move — confirms onRefresh actually fires,
            not just that the link renders. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — tap &quot;Refresh&quot; (home&apos;s Editorial Picks, light
              surface) · fired {refreshCount}×
            </Text>
            <PSHomeSectionEmpty
              onRefresh={() => setRefreshCount((c) => c + 1)}
            />
          </YStack>

          {/* isBackgroundDark — memberships' showcase/all-plans sections, all drawn on
            `$color.brandDark`. The cat's tintColor swaps from brand purple
            to a muted border-gray here (see the component's own tintColor
            line) so it stays legible against the dark fill instead of
            vanishing into it. Same live wiring as the light block above. */}
          <YStack bg="$color.brandDark" p="$space.xl" gap="$lg" rounded="$base">
            <Text fontFamily="$body" fontSize="$small" color="$textWhiteMuted">
              live — isBackgroundDark (showcase, all plans) · fired{' '}
              {refreshCount}×
            </Text>
            <PSHomeSectionEmpty
              onRefresh={() => setRefreshCount((c) => c + 1)}
              isBackgroundDark
            />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
